// Every build writes its chunks under a fresh `/<Date.now()>/` folder (vite.config.ts), and the
// nginx image only ships the current one. A tab opened before a deploy still holds the old module
// graph, so its next lazy import 404s. Reloading fetches the new index.html; the timestamp guard
// stops a reload loop when an asset is genuinely missing rather than merely superseded.
//
// The guard lives in localStorage, NOT sessionStorage: `src/layout/global-right-section.tsx` calls
// sessionStorage.clear() whenever no panel is open — i.e. on every page load — which would erase
// the guard immediately after the reload it is meant to limit.

const RELOAD_KEY = 'chunk-reload-at';
export const RELOAD_GUARD_MS = 10_000;

const CHUNK_ERROR_PATTERNS = [
	/Failed to fetch dynamically imported module/i, // Chromium
	/Importing a module script failed/i, // Safari
	/error loading dynamically imported module/i, // Firefox
	/Unable to preload CSS/i, // Vite CSS preload
	/ChunkLoadError/i,
];

export const isChunkLoadError = (error: unknown): boolean => {
	if (!error) return false;
	const name = (error as { name?: unknown }).name;
	const message = (error as { message?: unknown }).message;
	const text = `${typeof name === 'string' ? name : ''} ${typeof message === 'string' ? message : String(error)}`;
	return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
};

const readLastReload = (): number => {
	try {
		return Number(window.localStorage.getItem(RELOAD_KEY)) || 0;
	} catch {
		return 0;
	}
};

/** True when a stale-build reload already happened moments ago, i.e. reloading again would loop. */
export const hasRecentlyReloaded = (): boolean => Date.now() - readLastReload() < RELOAD_GUARD_MS;

/** Reloads the page at most once per guard window. Returns whether a reload was triggered. */
export const reloadOnceForStaleBuild = (): boolean => {
	if (hasRecentlyReloaded()) return false;
	try {
		window.localStorage.setItem(RELOAD_KEY, String(Date.now()));
	} catch {
		// Storage unavailable (private mode / blocked): still reload once; the worst case is one
		// extra reload per failed import, never a tight loop, because the import itself must fail.
	}
	window.location.reload();
	return true;
};
